/**
 * The learner's session page, in words (board 2). Every function is a lookup
 * over server-derived fields: none compares a clock, ranks an act, or decides
 * what a learner may do. `past_due` is the database's word, `primary_work_id`
 * the server's pick, and `open_session_activity` the server's ranked act.
 */
import type { AttendanceStatus } from "@/lib/api/attendance";
import type { LearnerAssignmentState, LearnerSessionAssignment, SessionFoot, SessionNeighbour, SessionWorkspace } from "@/lib/api/sessionPortal";

/** What the foot says of a session: the fields a neighbour and the server's foot share. */
export type FootSession = Pick<SessionNeighbour, "id" | "session_no" | "label" | "starts_at" | "timezone" | "latest_change" | "destination">;
import { EXPECTED_DELIVERABLE_LABEL } from "@/lib/assignment-presentation";
import { classTitle } from "@/lib/class-name";
import { ATTENDANCE_STATUS_LABEL, formatInCourseZone, sessionChangeWords } from "@/lib/session-portal";

/** The state a learner reads, one plain word (ADR 0035 decision 6: never a chip). */
export const WORK_STATE_WORD: Record<LearnerAssignmentState, string> = {
  not_submitted: "Not handed in",
  submitted: "Handed in",
  returned: "Returned for revision",
  resubmitted: "Handed in",
  graded: "Graded",
};

/** Whether an attempt is still owed on this row, as the row's own fields say it. */
export function owesAnAttempt(work: LearnerSessionAssignment): boolean {
  return work.status === "published" && (work.state === "not_submitted" || work.resubmission_open);
}

/** The act's name: board 4's, with the attempt only when it is not the first. */
export function handInLabel(work: LearnerSessionAssignment): string {
  return work.state === "returned" ? `Review and hand in attempt ${work.attempt_count + 1}` : "Review and hand in";
}

/**
 * What waits, in the foot beside the act. Work never handed in already reads
 * Not handed in on its row, so the foot adds a line only for a further attempt.
 */
export function waitingLine(work: LearnerSessionAssignment): string | null {
  return work.state === "not_submitted" ? null : `Attempt ${work.attempt_count + 1} not handed in`;
}

const DAY = { weekday: "short", month: "short", day: "numeric" } as const;
const DAY_TIME = { ...DAY, hour: "numeric", minute: "2-digit" } as const;

/** The work line in parts, so the page can mark the due time up as a time. */
export function workFacts(work: LearnerSessionAssignment, timezone: string): { dueAt: string | null; due: string; rest: string[] } {
  const rest: string[] = [];
  if (work.due_at && work.past_due && owesAnAttempt(work)) rest.push("Past due");
  if (work.points != null) rest.push(`${work.points} ${work.points === 1 ? "point" : "points"}`);
  if (work.expected_deliverable) rest.push(EXPECTED_DELIVERABLE_LABEL[work.expected_deliverable]);
  return { dueAt: work.due_at, due: work.due_at ? `Due ${formatInCourseZone(work.due_at, timezone, DAY_TIME)}` : "No due date", rest };
}

/** `Due Thu, Oct 22, 10:54 AM · Past due · 20 points · Structured writing`. */
export function workLine(work: LearnerSessionAssignment, timezone: string): string {
  const { due, rest } = workFacts(work, timezone);
  return [due, ...rest].join(" · ");
}

/** `Attempt 2 · Mon, Oct 19, 12:05 PM · With your instructor`, once something has been handed in. */
export function attemptLine(work: LearnerSessionAssignment, timezone: string): string | null {
  if (!work.attempt_count || !work.latest_attempt_at) return null;
  const waiting = work.state === "submitted" || work.state === "resubmitted" ? " · With your instructor" : "";
  return `Attempt ${work.attempt_count} · ${formatInCourseZone(work.latest_attempt_at, timezone, DAY_TIME)}${waiting}`;
}

/** The brief's first paragraph, shown until the first hand-in. */
export function briefLine(work: LearnerSessionAssignment): string | null {
  if (work.attempt_count > 0) return null;
  return work.instructions?.split(/\n\s*\n/)[0]?.trim() || null;
}

/** The quiet route into work with no act: board 2's two names. */
export function openWorkLabel(work: LearnerSessionAssignment): string {
  return work.attempt_count > 0 ? "Open your work" : "Open the work";
}

/** The day and time a fact happened, in the course's zone. */
export function whenWords(instant: string, timezone: string): string {
  return formatInCourseZone(instant, timezone, DAY_TIME);
}

/** The foot's cancelled line: number, day, word, and the teacher's note when there is one. */
export function cancelledLine(item: FootSession): string {
  const name = item.session_no != null ? `Session ${item.session_no}` : item.label?.trim() || "Session";
  const note = item.latest_change?.kind === "cancel" ? item.latest_change.note : null;
  return `${name} · ${formatInCourseZone(item.starts_at, item.timezone, DAY)} · Cancelled${note ? ` · ${note}` : ""}`;
}

/** The teacher's note on this session's change, said once on the page: Does not meet for a cancel, Get ready for a move. */
export function changeNote(workspace: SessionWorkspace): string | null {
  const change = workspace.session.latest_change;
  return change?.note?.trim() ? change.note.trim() : null;
}

/** Your attendance, only once the session is held; a learner the roll left unmarked reads Not recorded. */
export function attendanceLine(workspace: SessionWorkspace): string | null {
  const { attendance, session } = workspace;
  if (attendance.kind !== "own" || session.status !== "held") return null;
  return `Your attendance: ${attendance.status ? ATTENDANCE_STATUS_LABEL[attendance.status as AttendanceStatus] : "Not recorded"}`;
}

/** The folded After class line; board 5's Follow up reuses it. */
export function afterClassSummary(workspace: SessionWorkspace): string {
  const { plan } = workspace;
  const posted = Boolean(plan.unit?.recap?.trim() || plan.unit?.follow_up?.trim());
  return [attendanceLine(workspace), posted ? "Recap and next steps posted" : "No recap or next steps posted"]
    .filter(Boolean).join(" · ");
}

/** The foot: the session before that met, the cancelled ones in between, and Next scheduled. */
export function learnerFoot(workspace: SessionWorkspace): {
  previous: FootSession | null;
  cancelled: FootSession[];
  next: (FootSession & { firstLine?: string | null }) | null;
} | null {
  const sequence = workspace.sequence;
  if (sequence?.state !== "ready") return null;
  const { sessions } = sequence.value;
  const at = sessions.findIndex((item) => item.id === workspace.session.id);
  if (at < 0) return null;
  const before = sessions.slice(0, at);
  const after = sessions.slice(at + 1);
  const next = after.find((item) => item.institutional_status === "scheduled") ?? null;
  const between = next ? after.slice(0, after.indexOf(next)) : after;
  return {
    previous: [...before].reverse().find((item) => item.institutional_status !== "cancelled") ?? null,
    cancelled: between.filter((item) => item.institutional_status === "cancelled"),
    next,
  };
}

/** `Session 7 · Overflow room, Fri, Oct 23, 10:00 AM`. */
export function neighbourWords(item: FootSession): string {
  const moved = item.latest_change?.kind === "move" ? sessionChangeWords(item.latest_change, false) : null;
  return `${classTitle({ session_no: item.session_no, label: item.label, starts_at: item.starts_at, session_date: null }, item.timezone)}, ${formatInCourseZone(item.starts_at, item.timezone, DAY_TIME)}${moved ? ` · ${moved}` : ""}`;
}

/**
 * The foot the server reads forward (`continuation.foot`): the last session that
 * met, the cancelled ones between it and this session, and Next scheduled with
 * the first line of its Before the session. An unreadable previous or next
 * establishes no edge, so the whole foot says it could not be read.
 */
export function learnerFootFromServer(foot: SessionFoot): {
  previous: FootSession | null;
  cancelled: FootSession[];
  next: (FootSession & { firstLine: string | null }) | null;
} | null {
  const ahead = foot.next_scheduled.state === "ready" ? foot.next_scheduled.value : null;
  const plan = ahead?.plan.state === "ready" && "first_line" in ahead.plan.value ? ahead.plan.value.first_line : null;
  return {
    previous: foot.previous.state === "ready" ? foot.previous.value : null,
    cancelled: foot.cancelled.state === "ready" ? foot.cancelled.value : [],
    next: ahead ? { ...ahead, firstLine: plan?.trim() || null } : null,
  };
}
